import React, { useState, useRef, useEffect, useCallback } from 'react';
import { aiAssistantAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Bot, Send, Minimize2, Sparkles, User, Loader2, MessageSquarePlus, RotateCcw, Mic, MicOff, Paperclip, X, Download, BarChart3, FileSpreadsheet, FileText } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const CHART_COLORS = ['#800000', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'];

// Renders markdown text with proper tables, bold, code
function MessageContent({ text, isUser }) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    // Detect markdown table (lines starting with |)
    if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      if (tableLines.length >= 2) {
        elements.push(<MarkdownTable key={`tbl-${i}`} lines={tableLines} isUser={isUser} />);
        continue;
      }
    }
    // Bullet points
    if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
      const bulletText = line.trim().substring(2);
      elements.push(<div key={i} className="flex gap-1.5 mt-0.5"><span className="text-[#800000] mt-0.5 shrink-0">&#8226;</span><span>{renderInline(bulletText)}</span></div>);
    } else if (line.trim() === '') {
      elements.push(<div key={i} className="h-1" />);
    } else {
      elements.push(<p key={i} className={i > 0 ? 'mt-1' : ''}>{renderInline(line)}</p>);
    }
    i++;
  }
  return <>{elements}</>;
}

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, k) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={k}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`')) return <code key={k} className="bg-gray-200/50 px-1 rounded text-xs font-mono">{part.slice(1, -1)}</code>;
    return part;
  });
}

function MarkdownTable({ lines, isUser }) {
  // Parse header row
  const headerCells = lines[0].split('|').filter(c => c.trim()).map(c => c.trim());
  // Skip separator row (line with dashes)
  const dataStartIdx = lines[1]?.includes('---') ? 2 : 1;
  const rows = lines.slice(dataStartIdx).map(l => l.split('|').filter(c => c.trim()).map(c => c.trim()));

  return (
    <div className="mt-2 mb-1 overflow-x-auto rounded-lg border border-gray-200/50">
      <table className="w-full text-[11px]">
        <thead>
          <tr className={isUser ? 'bg-white/10' : 'bg-white'}>
            {headerCells.map((h, i) => (
              <th key={i} className={`px-2 py-1.5 font-semibold text-left border-b ${isUser ? 'border-white/20 text-white/90' : 'border-gray-200 text-gray-700'}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className={isUser ? 'hover:bg-white/5' : `${ri % 2 === 0 ? 'bg-gray-50/50' : 'bg-white'} hover:bg-gray-100/50`}>
              {row.map((cell, ci) => (
                <td key={ci} className={`px-2 py-1 ${isUser ? 'border-b border-white/10 text-white/80' : 'border-b border-gray-100 text-gray-600'} ${ci > 0 && /^[\d,.\s]+$/.test(cell.replace(/OMR\s?/g, '')) ? 'text-right font-mono' : ''}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InlineChart({ chartData }) {
  if (!chartData) return null;
  const { type, title, data } = chartData;
  return (
    <div className="mt-2 p-2 bg-white rounded-lg border">
      {title && <p className="text-[10px] font-semibold text-gray-600 mb-1">{title}</p>}
      <div className="h-32">
        <ResponsiveContainer>
          {type === 'pie' ? (
            <PieChart><Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={50} label={({ name, percent }) => `${name.substring(0, 8)} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={8}>
              {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie><Tooltip formatter={(v) => v.toLocaleString()} /></PieChart>
          ) : type === 'line' ? (
            <LineChart data={data}><XAxis dataKey="name" fontSize={8} /><YAxis fontSize={8} /><Tooltip /><Line type="monotone" dataKey="value" stroke="#800000" strokeWidth={2} dot={{ r: 2 }} /></LineChart>
          ) : (
            <BarChart data={data}><XAxis dataKey="name" fontSize={8} /><YAxis fontSize={8} /><Tooltip /><Bar dataKey="value" fill="#800000" radius={[2, 2, 0, 0]}>
              {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Bar></BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function parseChartFromText(text) {
  if (!text || typeof text !== 'string') return { cleanText: text || '', chart: null };
  const chartMatch = text.match(/```chart\s*\n?([\s\S]*?)\n?```/);
  if (!chartMatch) return { cleanText: text, chart: null };
  try {
    const chart = JSON.parse(chartMatch[1]);
    const cleanText = text.replace(/```chart\s*\n?[\s\S]*?\n?```/, '').trim();
    return { cleanText, chart };
  } catch { return { cleanText: text, chart: null }; }
}

const FEEDBACK_KEYWORDS = ['feedback', 'bug', 'issue', 'report', 'suggestion', 'broken', 'not working', 'wrong', 'error', 'fix', 'problem'];
function detect_feedback_intent_client(text) {
  const q = (text || '').toLowerCase();
  return FEEDBACK_KEYWORDS.some(kw => q.includes(kw));
}

export default function AiAssistantBubble() {
  const readStoredPosition = (key, fallbackX, fallbackY) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return { x: fallbackX, y: fallbackY };
      const parsed = JSON.parse(raw);
      if (typeof parsed?.x === 'number' && typeof parsed?.y === 'number') return parsed;
    } catch {}
    return { x: fallbackX, y: fallbackY };
  };
  const panelWidth = 420;
  const panelHeight = 580;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', text: "Hi! I'm your CRM AI Assistant. Ask me anything about your pipeline, invoices, activities, or performance.\n\nYou can also share feedback (with screenshots) or use voice input." }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('auto');
  const [aiStatus, setAiStatus] = useState(null); // null=unchecked, 'ok'=healthy, 'error'=degraded
  // Stable session per browser — persists across page reloads for conversation memory
  const [sessionId] = useState(() => {
    const stored = localStorage.getItem('ai_session_id');
    if (stored) return stored;
    const newId = `crm-${Date.now()}`;
    localStorage.setItem('ai_session_id', newId);
    return newId;
  });
  const [recording, setRecording] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const scrollRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const dragMetaRef = useRef({ type: null, startX: 0, startY: 0, originX: 0, originY: 0, moved: false, suppressClick: false });
  const [fabPosition, setFabPosition] = useState(() => readStoredPosition('ai_assistant_fab_position', Math.max(16, window.innerWidth - 92), Math.max(16, window.innerHeight - 92)));
  const [panelPosition, setPanelPosition] = useState(() => readStoredPosition('ai_assistant_panel_position', Math.max(16, window.innerWidth - 452), Math.max(16, window.innerHeight - 620)));

  const clampPosition = useCallback((x, y, width, height) => {
    const maxX = Math.max(16, window.innerWidth - width - 16);
    const maxY = Math.max(16, window.innerHeight - height - 16);
    return {
      x: Math.min(Math.max(16, x), maxX),
      y: Math.min(Math.max(16, y), maxY),
    };
  }, []);

  useEffect(() => { localStorage.setItem('ai_assistant_fab_position', JSON.stringify(fabPosition)); }, [fabPosition]);
  useEffect(() => { localStorage.setItem('ai_assistant_panel_position', JSON.stringify(panelPosition)); }, [panelPosition]);

  useEffect(() => {
    const onMouseMove = (event) => {
      if (!dragMetaRef.current.type) return;
      const dx = event.clientX - dragMetaRef.current.startX;
      const dy = event.clientY - dragMetaRef.current.startY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) dragMetaRef.current.moved = true;
      if (dragMetaRef.current.type === 'fab') {
        setFabPosition(clampPosition(dragMetaRef.current.originX + dx, dragMetaRef.current.originY + dy, 64, 64));
      } else if (dragMetaRef.current.type === 'panel') {
        setPanelPosition(clampPosition(dragMetaRef.current.originX + dx, dragMetaRef.current.originY + dy, Math.min(panelWidth, window.innerWidth - 24), Math.min(panelHeight, window.innerHeight - 24)));
      }
    };
    const onMouseUp = () => {
      if (dragMetaRef.current.type === 'fab' && !dragMetaRef.current.moved) {
        setOpen(true);
      }
      dragMetaRef.current = { type: null, startX: 0, startY: 0, originX: 0, originY: 0, moved: false, suppressClick: dragMetaRef.current.moved };
      window.setTimeout(() => { dragMetaRef.current.suppressClick = false; }, 0);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [clampPosition]);

  const startDrag = (type, event) => {
    event.preventDefault();
    const origin = type === 'fab' ? fabPosition : panelPosition;
    dragMetaRef.current = { type, startX: event.clientX, startY: event.clientY, originX: origin.x, originY: origin.y, moved: false, suppressClick: false };
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Check AI service health when chat opens
  useEffect(() => {
    if (open && aiStatus === null) {
      aiAssistantAPI.health().then(r => {
        setAiStatus(r.data?.status === 'ok' ? 'ok' : 'error');
        if (r.data?.status !== 'ok') {
          console.warn('AI service health check failed:', r.data?.reason);
        }
      }).catch(() => setAiStatus('unknown'));
    }
  }, [open]); // eslint-disable-line


  const handleSend = async () => {
    if ((!input.trim() && attachments.length === 0) || loading) return;
    const question = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: question || '(attachment)', attachmentCount: attachments.length }]);
    setLoading(true);

    try {
      let data;
      if (attachments.length > 0 && (mode === 'feedback' || detect_feedback_intent_client(question))) {
        // Has attachments — submit as feedback with attachments
        const res = await aiAssistantAPI.submitFeedbackWithAttachment(question, sessionId, attachments);
        data = res.data;
      } else {
        const res = await aiAssistantAPI.chat(question, sessionId, mode);
        data = res.data;
      }
      const { cleanText, chart } = parseChartFromText(data.answer || data.response || '');
      const msg = { role: 'assistant', text: cleanText, chart };
      if (data.feedback_submitted) msg.feedbackId = data.feedback_id;
      // Mark error messages with a flag for styling
      if (data._error) msg.isError = true;
      setMessages(prev => [...prev, msg]);
      if (data.feedback_submitted) { setMode('auto'); setAttachments([]); }
      // Update AI status if error
      if (data._error) setAiStatus('error');
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Connection failed. Please check your internet connection and try again.', isError: true }]);
      setAiStatus('error');
    } finally { setLoading(false); }
  };

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 1000) return;
        setLoading(true);
        setMessages(prev => [...prev, { role: 'user', text: '(voice message)', isVoice: true }]);
        try {
          const res = await aiAssistantAPI.transcribeVoice(blob);
          const transcribed = res.data?.text || '';
          if (transcribed) {
            setMessages(prev => { const updated = [...prev]; updated[updated.length - 1].text = transcribed; return updated; });
            const chatRes = await aiAssistantAPI.chat(transcribed, sessionId, mode);
            const { cleanText, chart } = parseChartFromText(chatRes.data.answer);
            const msg = { role: 'assistant', text: cleanText, chart };
            if (chatRes.data.feedback_submitted) msg.feedbackId = chatRes.data.feedback_id;
            setMessages(prev => [...prev, msg]);
          } else {
            setMessages(prev => [...prev, { role: 'assistant', text: "I couldn't understand the audio. Please try again or type your question." }]);
          }
        } catch { setMessages(prev => [...prev, { role: 'assistant', text: 'Voice transcription failed. Please try typing.' }]); }
        finally { setLoading(false); }
      };
      mediaRecorder.start();
      setRecording(true);
    } catch { setMessages(prev => [...prev, { role: 'assistant', text: 'Microphone access denied. Please allow microphone access in your browser.' }]); }
  }, [sessionId, mode]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  }, [recording]);

  const switchToFeedback = () => {
    setMode('feedback');
    setMessages(prev => [...prev, { role: 'assistant', text: "I'm ready to collect your feedback. Describe the issue and optionally attach screenshots using the paperclip button.\n\nTip: Include the module name and priority if possible." }]);
  };

  const resetChat = () => {
    setMessages([{ role: 'assistant', text: "Hi! I'm your CRM AI Assistant. Ask me anything about your pipeline, invoices, activities, or performance.\n\nYou can also share feedback (with screenshots) or use voice input." }]);
    setMode('auto'); setAttachments([]);
    // New session for fresh conversation
    const newId = `crm-${Date.now()}`;
    localStorage.setItem('ai_session_id', newId);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3);
    setAttachments(prev => [...prev, ...files].slice(0, 3));
    e.target.value = '';
  };

  const removeAttachment = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  const exportChatAsText = () => {
    const text = messages.map(m => `[${m.role}]: ${m.text}`).join('\n\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'crm-chat-export.txt'; a.click();
    URL.revokeObjectURL(url);
  };

  const [showReportPanel, setShowReportPanel] = useState(false);
  const [exportingReport, setExportingReport] = useState(null);

  const handleExportReport = async (format, type) => {
    setExportingReport(`${type}-${format}`);
    try {
      const res = await aiAssistantAPI.exportReport(format, type, '2026');
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${type}_report_2026.${ext}`; a.click();
      URL.revokeObjectURL(url);
      setMessages(prev => [...prev, { role: 'assistant', text: `Report exported: **${type.charAt(0).toUpperCase() + type.slice(1)} Report (${format.toUpperCase()})** downloaded successfully.` }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Failed to export report. Please try again.' }]);
    } finally { setExportingReport(null); }
  };

  const quickQuestions = [
    "What's my pipeline for 2026?",
    "Show overdue invoices",
    "Team performance summary",
  ];

  if (!open) {
    return (
      <button onMouseDown={(e) => startDrag('fab', e)} onClick={(e) => { if (dragMetaRef.current.suppressClick) { e.preventDefault(); return; } setOpen(true); }} data-testid="ai-assistant-fab"
        className="fixed z-50 p-3.5 rounded-full bg-gradient-to-br from-[#800000] to-[#5a0000] text-white shadow-xl hover:shadow-2xl transition-all hover:scale-110 group cursor-move"
        style={{ left: fabPosition.x, top: fabPosition.y }}
        title="AI CRM Assistant & Feedback">
        <Bot className="h-6 w-6 group-hover:animate-pulse" />
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
      </button>
    );
  }

  return (
    <div className="fixed z-50 w-[420px] max-w-[calc(100vw-24px)] h-[580px] max-h-[calc(100vh-24px)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
      style={{ left: panelPosition.x, top: panelPosition.y }} data-testid="ai-assistant-panel">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#800000] to-[#5a0000] px-4 py-3 flex items-center gap-3 cursor-move select-none" onMouseDown={(e) => startDrag('panel', e)}>
        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
          <Bot className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1">
          <p className="text-white font-semibold text-sm">CRM AI Assistant</p>
          <p className="text-white/60 text-[10px]">
            {mode === 'feedback' ? 'Feedback Mode — attach screenshots' : 'Ask anything · Voice · Charts · Feedback'}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={exportChatAsText} className="p-1 rounded-lg hover:bg-white/10" title="Export chat"><Download className="h-3.5 w-3.5 text-white/80" /></button>
          <button onClick={resetChat} className="p-1 rounded-lg hover:bg-white/10" title="New chat"><RotateCcw className="h-3.5 w-3.5 text-white/80" /></button>
          <button onClick={() => { setFabPosition(clampPosition(panelPosition.x + Math.min(panelWidth, window.innerWidth - 24) - 72, panelPosition.y + Math.min(panelHeight, window.innerHeight - 24) - 72, 64, 64)); setOpen(false); }} className="p-1 rounded-lg hover:bg-white/10" title="Minimize"><Minimize2 className="h-4 w-4 text-white/80" /></button>
        </div>
      </div>

      {/* Mode indicator */}
      {mode === 'feedback' && (
        <div className="px-3 py-1.5 bg-amber-50 border-b border-amber-100 flex items-center justify-between">
          <span className="text-[10px] font-medium text-amber-700 flex items-center gap-1">
            <MessageSquarePlus className="h-3 w-3" /> Feedback Mode — attach screenshots with paperclip
          </span>
          <button onClick={() => { setMode('auto'); setAttachments([]); }} className="text-[10px] text-amber-600 hover:text-amber-800 underline">Switch to CRM</button>
        </div>
      )}

      {/* AI Service Status Banner */}
      {aiStatus === 'error' && (
        <div className="px-3 py-2 bg-red-50 border-b border-red-100 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[10px] text-red-700">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
            <span className="font-medium">AI service issue detected.</span>
            <span>Check Profile → Universal Key balance.</span>
          </div>
          <a href="https://app.emergentagent.com" target="_blank" rel="noopener noreferrer"
            className="text-[10px] text-red-600 underline whitespace-nowrap font-medium hover:text-red-800">
            Add Balance
          </a>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.filter(Boolean).map((msg, i) => (
          <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && (
              <div className="w-7 h-7 rounded-full bg-[#800000]/10 flex items-center justify-center shrink-0 mt-0.5">
                <Sparkles className="h-3.5 w-3.5 text-[#800000]" />
              </div>
            )}
            <div className={`max-w-[90%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
              msg.role === 'user' ? 'bg-[#800000] text-white rounded-br-md' :
              msg.isError ? 'bg-red-50 text-red-800 border border-red-200 rounded-bl-md' :
              'bg-gray-100 text-gray-800 rounded-bl-md'
            }`}>
              {msg.isVoice && <Badge variant="outline" className="text-[9px] mb-1 border-white/30 text-white/70"><Mic className="h-2 w-2 mr-0.5" />Voice</Badge>}
              <MessageContent text={msg?.text || ''} isUser={msg?.role === 'user'} />
              {msg.attachmentCount > 0 && <Badge variant="outline" className="mt-1 text-[9px] border-white/30 text-white/70"><Paperclip className="h-2 w-2 mr-0.5" />{msg.attachmentCount} file(s)</Badge>}
              {msg.chart && <InlineChart chartData={msg.chart} />}
              {msg.feedbackId && <Badge variant="outline" className="mt-2 text-[10px] bg-green-50 border-green-200 text-green-700">Feedback submitted</Badge>}
            </div>
            {msg.role === 'user' && (
              <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center shrink-0 mt-0.5">
                <User className="h-3.5 w-3.5 text-gray-600" />
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex gap-2">
            <div className="w-7 h-7 rounded-full bg-[#800000]/10 flex items-center justify-center shrink-0">
              <Sparkles className="h-3.5 w-3.5 text-[#800000] animate-pulse" />
            </div>
            <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
              <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
            </div>
          </div>
        )}
      </div>

      {/* Quick Questions + Feedback + Reports (show only at start) */}
      {messages.length <= 1 && (
        <div className="px-4 pb-2 space-y-1.5">
          <div className="flex flex-wrap gap-1.5">
            {quickQuestions.map((q, i) => (
              <button key={i} onClick={() => setInput(q)}
                className="text-[10px] px-2.5 py-1 rounded-full border border-[#800000]/20 text-[#800000] hover:bg-[#800000]/5 transition-colors">{q}</button>
            ))}
            <button onClick={() => setInput("Show me a pipeline chart by stage")}
              className="text-[10px] px-2.5 py-1 rounded-full border border-[#800000]/20 text-[#800000] hover:bg-[#800000]/5 transition-colors flex items-center gap-1">
              <BarChart3 className="h-2.5 w-2.5" /> Pipeline chart
            </button>
          </div>
          <div className="flex gap-1.5">
            <button onClick={switchToFeedback} data-testid="ai-feedback-mode-btn"
              className="flex-1 text-[10px] px-2.5 py-1.5 rounded-lg border border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors flex items-center justify-center gap-1">
              <MessageSquarePlus className="h-3 w-3" /> Feedback
            </button>
            <button onClick={() => setShowReportPanel(!showReportPanel)} data-testid="ai-report-export-btn"
              className="flex-1 text-[10px] px-2.5 py-1.5 rounded-lg border border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors flex items-center justify-center gap-1">
              <FileSpreadsheet className="h-3 w-3" /> Export Reports
            </button>
          </div>
        </div>
      )}

      {/* Report Export Panel */}
      {showReportPanel && (
        <div className="px-4 pb-2">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-2">
            <p className="text-[10px] font-semibold text-emerald-800">Export CRM Report</p>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                { type: 'pipeline', label: 'Pipeline' },
                { type: 'invoices', label: 'Invoices' },
                { type: 'performance', label: 'Performance' },
                { type: 'activities', label: 'Activities' },
              ].map(r => (
                <div key={r.type} className="flex gap-1">
                  <button onClick={() => handleExportReport('excel', r.type)} disabled={!!exportingReport}
                    data-testid={`export-${r.type}-excel`}
                    className="flex-1 text-[9px] px-2 py-1.5 rounded-lg border border-green-200 bg-white text-green-700 hover:bg-green-50 transition-colors flex items-center justify-center gap-1 disabled:opacity-50">
                    {exportingReport === `${r.type}-excel` ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <FileSpreadsheet className="h-2.5 w-2.5" />} {r.label}
                  </button>
                  <button onClick={() => handleExportReport('pdf', r.type)} disabled={!!exportingReport}
                    data-testid={`export-${r.type}-pdf`}
                    className="text-[9px] px-2 py-1.5 rounded-lg border border-red-200 bg-white text-red-700 hover:bg-red-50 transition-colors flex items-center justify-center gap-1 disabled:opacity-50">
                    {exportingReport === `${r.type}-pdf` ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <FileText className="h-2.5 w-2.5" />}
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => setShowReportPanel(false)} className="text-[9px] text-gray-500 hover:text-gray-700 underline">Close</button>
          </div>
        </div>
      )}

      {/* Attachment preview */}
      {attachments.length > 0 && (
        <div className="px-3 pb-1 flex gap-1.5 overflow-x-auto">
          {attachments.map((f, i) => (
            <div key={i} className="relative shrink-0 px-2 py-1 bg-gray-100 rounded-lg text-[10px] text-gray-600 flex items-center gap-1">
              <Paperclip className="h-2.5 w-2.5" /> {f.name?.substring(0, 15) || 'file'}
              <button onClick={() => removeAttachment(i)} className="ml-0.5 hover:text-red-500"><X className="h-2.5 w-2.5" /></button>
            </div>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="p-3 border-t bg-gray-50">
        <div className="flex gap-1.5">
          {/* Attach button (always available) */}
          <button onClick={() => fileInputRef.current?.click()} className="h-10 w-10 flex items-center justify-center rounded-xl border bg-white hover:bg-gray-50 shrink-0" title="Attach file" data-testid="ai-attach-btn">
            <Paperclip className="h-4 w-4 text-gray-500" />
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} />
          
          <Input value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSend(); }}
            placeholder={mode === 'feedback' ? 'Describe your feedback...' : 'Ask about CRM data or use mic...'}
            className="flex-1 h-10 text-sm rounded-xl bg-white"
            disabled={loading || recording}
            data-testid="ai-assistant-input" />
          
          {/* Voice button */}
          <button onClick={recording ? stopRecording : startRecording}
            className={`h-10 w-10 flex items-center justify-center rounded-xl border shrink-0 transition-colors ${recording ? 'bg-red-500 border-red-500 text-white animate-pulse' : 'bg-white hover:bg-gray-50 text-gray-500'}`}
            disabled={loading} title={recording ? 'Stop recording' : 'Voice input'} data-testid="ai-voice-btn">
            {recording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </button>
          
          <Button onClick={handleSend} disabled={loading || (!input.trim() && attachments.length === 0)}
            className="h-10 w-10 p-0 rounded-xl bg-[#800000] hover:bg-[#9a1919] shrink-0"
            data-testid="ai-assistant-send">
            <Send className="h-4 w-4 text-white" />
          </Button>
        </div>
      </div>
    </div>
  );
}
